import React, { useEffect, useMemo, useState, useRef } from 'react';
import { CalculatorInputs } from '../components/CalculatorInputs';
import { AssumptionsSidebar } from '../components/AssumptionsSidebar';
import { ResultsSummary } from '../components/ResultsSummary';
import { ProfitChart } from '../components/ProfitChart';
import { ProjectionTable } from '../components/ProjectionTable';
import { HiringFunnelModal } from '../components/HiringFunnelModal';
import { ExpandableHiringFunnel } from '../components/ExpandableHiringFunnel';
import { RepEconomics } from '../components/RepEconomics';
import { Inputs, DEFAULT_INPUTS, calculateModel, PackageType, BuildPayType, IndustryType, optimizeCampaignsForRevenueTarget, optimizeCampaignsForTeamTarget, salesPerNinetyDayRep, Timeframe, usesRevenueTrail } from '../utils/roiCalculations';
import { ConversionRates, DEFAULT_CONVERSION_RATES, computeOverhead, ADMIN_HOURLY_RATE, LEADERSHIP_HOURLY_RATE } from '../utils/hiringFunnel';
import { fmtMoney } from '../utils/formatters';
import { usePricing } from '../contexts/PricingContext';
import { INITIAL_PRESET_STATE, applyBasePreset, reconcilePresets, togglePlusFive } from '../utils/retentionPresets';
const CORRECT_PASSWORD = 'HIRE100k';
const ADDITIONAL_PASSWORDS = ['mymanny', '1234', 'temp1234'];
const TEMP_PASSWORD = 'UNTD.solar';
const TEMP_PASSWORD_START = new Date('2026-02-02T00:00:00');
const TEMP_PASSWORD_END = new Date('2026-02-07T00:00:00'); // End of Feb 6th (midnight Feb 7th)
const TEMP_PASSWORD_2 = 'Polaris';
const TEMP_PASSWORD_2_START = new Date('2026-03-27T00:00:00');
const TEMP_PASSWORD_2_END = new Date('2026-03-30T00:00:00'); // End of Mar 29th (midnight Mar 30th)
const TEMP_PASSWORD_3 = '1234';
const TEMP_PASSWORD_3_START = new Date('2026-04-07T00:00:00');
const TEMP_PASSWORD_3_END = new Date('2026-04-23T00:00:00'); // End of Apr 22nd (midnight Apr 23rd)
const AUTH_KEY = 'hire_calculator_authenticated';
const CAMPAIGN_SPEND_STEP = 2500;
// Ad cost per hire adjusts in $50 increments.
const CPH_STEP = 50;
// Inline numeric fields size themselves to their contents.
const fieldWidthCh = (value: string | number) => Math.max(2, String(value).length + 0.5);
// Tight variant: hugs the digits so a 3-digit value sits as close to the
// range dash as a 4-digit one, with only a hair of anti-clipping slack.
const tightFieldWidthCh = (value: string | number) => Math.max(1, String(value).length + 0.1);
// Hours read cleanest without a trailing ".0".
const formatHours = (hours: number) => (Math.round(hours * 10) / 10).toLocaleString(undefined, {
  maximumFractionDigits: 1
});
// Expected hires are shown as a range using a ±$150 band around the median
// ad spend cost per hire. The median itself drives the projection table.
const CPH_RANGE_SPREAD = 150;
const isValidPassword = (password: string): boolean => {
  // Check permanent passwords
  if (password === CORRECT_PASSWORD) {
    return true;
  }
  // Check additional permanent passwords
  if (ADDITIONAL_PASSWORDS.includes(password)) {
    return true;
  }
  // Check temporary passwords within date range
  const now = new Date();
  if (password === TEMP_PASSWORD && now >= TEMP_PASSWORD_START && now < TEMP_PASSWORD_END) {
    return true;
  }
  if (password === TEMP_PASSWORD_2 && now >= TEMP_PASSWORD_2_START && now < TEMP_PASSWORD_2_END) {
    return true;
  }
  if (password === TEMP_PASSWORD_3 && now >= TEMP_PASSWORD_3_START && now < TEMP_PASSWORD_3_END) {
    return true;
  }
  return false;
};
export function ROICalculator() {
  // Set browser tab title
  useEffect(() => {
    document.title = 'Recruiting ROI Calculator';
  }, []);
  const [isClient, setIsClient] = useState(false);
  const [isLightMode, setIsLightMode] = useState(true);
  // Start from any assumptions already set elsewhere (e.g. the proposal's
  // Tailor panel) so opening the calculator doesn't reset them.
  const {
    calculatorInputs: initialSharedInputs,
    retentionPresets,
    setRetentionPresets
  } = usePricing();
  const [inputs, setInputs] = useState<Inputs>(() => initialSharedInputs ?? DEFAULT_INPUTS);
  const [hasCalculated, setHasCalculated] = useState(false);
  const [hasReviewed, setHasReviewed] = useState(false);
  // Sidebar state
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  // Shared state for toggles
  const [halfSales, setHalfSales] = useState(false);
  const [doubleAttrition, setDoubleAttrition] = useState(false);
  // Retention presets (Low / On Target / +5%) live in shared state so the
  // proposal's Tailor panel and the calculator always show the same selection.
  const {
    basePreset,
    plusFive
  } = retentionPresets;
  const onTarget = basePreset === 'onTarget';
  const lowRetention = basePreset === 'low';
  const [packageScenario, setPackageScenario] = useState<'doubleAttrition' | 'plusFive' | null>(null);
  const previousPackageRef = useRef<PackageType | null>(null);
  const [cphLowDraft, setCphLowDraft] = useState<string | null>(null);
  const [cphHighDraft, setCphHighDraft] = useState<string | null>(null);
  const [preOptimizeMonthlyCampaigns, setPreOptimizeMonthlyCampaigns] = useState<number[] | null>(null);
  const [timeframe, setTimeframe] = useState<Timeframe>(6);
  const [savePdfLabel, setSavePdfLabel] = useState('Save as PDF');
  const [showPackageTooltip, setShowPackageTooltip] = useState(false);
  const [showBuildTooltip, setShowBuildTooltip] = useState(false);
  const [expandedReviewSections, setExpandedReviewSections] = useState({
    campaignAdSpend: false,
    revenueMargins: false,
    salesPerformance: false,
    retentionModeling: false
  });
  const [showHiringFunnel, setShowHiringFunnel] = useState(false);
  const [showHiringMetrics, setShowHiringMetrics] = useState(false);
  const [showOverhead, setShowOverhead] = useState(false);
  // Recruiting funnel conversion rates (shared by the funnel + overhead math)
  const [funnelRates, setFunnelRates] = useState<ConversionRates>(DEFAULT_CONVERSION_RATES);
  const [reviewSpendInput, setReviewSpendInput] = useState('');
  const resultsRef = useRef<HTMLDivElement>(null);
  const reviewRef = useRef<HTMLDivElement>(null);
  // The deck already sits behind its own access code, so the calculator opens
  // straight away with no second login.
  useEffect(() => {
    setIsClient(true);
  }, []);
  // Load state from URL on mount
  useEffect(() => {
    if (!isClient) return;
    const params = new URLSearchParams(window.location.search);
    if ([...params.keys()].length === 0) return;
    const newInputs = {
      ...DEFAULT_INPUTS
    };
    const parseNum = (key: string, def: number) => {
      const val = params.get(key);
      return val ? parseFloat(val) : def;
    };
    if (params.has('pkg')) newInputs.pkg = params.get('pkg') as PackageType;
    if (params.has('buildPay')) newInputs.buildPay = params.get('buildPay') as BuildPayType;
    if (params.has('industry')) newInputs.industry = params.get('industry') as IndustryType;
    if (params.has('salesSchoolEnabled')) {
      newInputs.salesSchoolEnabled = params.get('salesSchoolEnabled') !== 'false';
    }
    newInputs.campaigns = parseNum('campaigns', DEFAULT_INPUTS.campaigns);
    newInputs.adSpend = parseNum('adSpend', DEFAULT_INPUTS.adSpend);
    newInputs.cph = parseNum('cph', DEFAULT_INPUTS.cph);
    newInputs.ticket = parseNum('ticket', DEFAULT_INPUTS.ticket);
    newInputs.gpm = parseNum('gpm', DEFAULT_INPUTS.gpm);
    newInputs.salesVetMonthly = parseNum('salesVetMonthly', DEFAULT_INPUTS.salesVetMonthly);
    newInputs.salesFirst90Total = parseNum('salesFirst90Total', DEFAULT_INPUTS.salesFirst90Total);
    newInputs.pctLaunched = parseNum('pctLaunched', DEFAULT_INPUTS.pctLaunched);
    newInputs.pct30 = parseNum('pct30', DEFAULT_INPUTS.pct30);
    newInputs.pct90 = parseNum('pct90', DEFAULT_INPUTS.pct90);
    newInputs.pct1y = parseNum('pct1y', DEFAULT_INPUTS.pct1y);
    newInputs.pullthrough = parseNum('pullthrough', DEFAULT_INPUTS.pullthrough);
    // Initialize monthly campaigns based on base campaigns if not present
    newInputs.monthlyCampaigns = Array(12).fill(newInputs.campaigns);
    newInputs.monthlyAdSpend = Array(12).fill(newInputs.adSpend);
    setInputs(newInputs);
    if (params.has('calculated') && params.get('calculated') === 'true') {
      setHasCalculated(true);
    }
  }, [isClient]);
  // Update URL when inputs change
  useEffect(() => {
    if (!isClient) return;
    const params = new URLSearchParams();
    Object.entries(inputs).forEach(([key, value]) => {
      // The pricing schedule never goes in the URL, so shared links and
      // reloads always open on the default schedule.
      if (key !== 'monthlyCampaigns' && key !== 'monthlyAdSpend' && key !== 'pricingModel') {
        // Don't clutter URL with arrays
        params.set(key, String(value));
      }
    });
    if (hasCalculated) {
      params.set('calculated', 'true');
    }
    const newUrl = `${window.location.pathname}?${params.toString()}`;
    window.history.replaceState({}, '', newUrl);
  }, [inputs, hasCalculated, isClient]);
  // Sync monthly campaigns when base campaigns change - ONE WAY ONLY
  useEffect(() => {
    // When base campaigns changes, ALWAYS update all monthly campaigns to match
    // This is a one-way sync: top → table only
    if (inputs.campaigns !== undefined) {
      // Only update if monthlyCampaigns doesn't exist or if all values match the old campaigns value
      // This allows manual edits in the table to persist
      const needsInit = !inputs.monthlyCampaigns || inputs.monthlyCampaigns.length !== 12;
      if (needsInit) {
        setInputs(prev => ({
          ...prev,
          monthlyCampaigns: Array(12).fill(inputs.campaigns)
        }));
      } else {
        // Check if all monthly values are the same (no manual edits)
        const allSame = inputs.monthlyCampaigns.every((c, i, arr) => c === arr[0]);
        // If all values are the same AND different from current campaigns input,
        // update them all to match the new campaigns value
        if (allSame && inputs.monthlyCampaigns[0] !== inputs.campaigns) {
          setInputs(prev => ({
            ...prev,
            monthlyCampaigns: Array(12).fill(inputs.campaigns)
          }));
        }
      }
    }
  }, [inputs.campaigns]);
  // Sync monthly ad spend when base ad spend changes - ONE WAY ONLY
  useEffect(() => {
    if (inputs.adSpend !== undefined) {
      const needsInit = !inputs.monthlyAdSpend || inputs.monthlyAdSpend.length !== 12;
      if (needsInit) {
        setInputs(prev => ({
          ...prev,
          monthlyAdSpend: Array(12).fill(inputs.adSpend)
        }));
      } else {
        // Check if all monthly values are the same (no manual edits)
        const allSame = inputs.monthlyAdSpend.every((c, i, arr) => c === arr[0]);
        // If all values are the same AND different from current ad spend input,
        // update them all to match the new ad spend value
        if (allSame && inputs.monthlyAdSpend[0] !== inputs.adSpend) {
          setInputs(prev => ({
            ...prev,
            monthlyAdSpend: Array(12).fill(inputs.adSpend)
          }));
        }
      }
    }
  }, [inputs.adSpend]);
  // Initialize if missing
  if (!inputs.monthlyCampaigns || inputs.monthlyCampaigns.length !== 12) {
    setInputs(prev => ({
      ...prev,
      monthlyCampaigns: Array(12).fill(prev.campaigns)
    }));
  }
  // Flagship (the day-45 retention bonus) is shared with the deck: clicking
  // "Flagship Partnership" there or the package badge here toggles both.
  const {
    pricingModel: deckPricingModel,
    setPricingModel: setDeckPricingModel
  } = usePricing();
  const calcPricingModel = deckPricingModel === 'flagship' ? 'legacy' : 'monthly';
  const calcInputs = useMemo(() => (inputs.pricingModel ?? 'monthly') === calcPricingModel ? inputs : {
    ...inputs,
    pricingModel: calcPricingModel
  }, [inputs, calcPricingModel]);
  // Calculate results - recalculates whenever inputs change (including monthlyCampaigns and monthlyAdSpend)
  const results = useMemo(() => {
    // Set default buildPay if not selected
    const inputsWithDefaults = {
      ...calcInputs,
      pkg: inputs.pkg,
      buildPay: inputs.buildPay || 'upfront'
    };
    return calculateModel(inputsWithDefaults);
  }, [calcInputs]);
  const toggleReviewSection = (section: keyof typeof expandedReviewSections) => {
    setExpandedReviewSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };
  const handleCalculate = () => {
    // Ensure monthly campaigns are initialized
    if (!inputs.monthlyCampaigns) {
      setInputs(prev => ({
        ...prev,
        monthlyCampaigns: Array(12).fill(prev.campaigns)
      }));
    }
    setHasCalculated(true);
    setTimeout(() => {
      resultsRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    }, 100);
  };
  const handleReview = () => {
    setHasReviewed(true);
    setTimeout(() => {
      reviewRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    }, 100);
  };
  const handleReset = () => {
    setInputs(DEFAULT_INPUTS);
    setHasCalculated(false);
    setHasReviewed(false);
    setHalfSales(false);
    setDoubleAttrition(false);
    setRetentionPresets(INITIAL_PRESET_STATE);
    setPackageScenario(null);
    previousPackageRef.current = DEFAULT_INPUTS.pkg;
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };
  // Scenarios applied on behalf of a package are cleared when the package
  // changes. Manual scenario changes clear this marker and remain untouched.
  useEffect(() => {
    const previousPackage = previousPackageRef.current;
    if (previousPackage === inputs.pkg) return;
    if (packageScenario === 'doubleAttrition') {
      setDoubleAttrition(false);
    }
    if (packageScenario === 'plusFive' && retentionPresets.plusFive) {
      const next = togglePlusFive(inputs, retentionPresets);
      setInputs(next.inputs);
      setRetentionPresets(next.state);
    }
    setPackageScenario(null);
    previousPackageRef.current = inputs.pkg;
  }, [inputs.pkg]);
  const handleDoubleAttritionChange = (value: boolean) => {
    setPackageScenario(scenario => scenario === 'doubleAttrition' ? null : scenario);
    setDoubleAttrition(value);
  };
  // Low and On Target are mutually exclusive retention curves. Selecting one
  // first unwinds whatever the current preset (and any +5% layer) changed.
  const handleBasePreset = (preset: 'low' | 'onTarget') => {
    setPackageScenario(scenario => scenario === 'plusFive' ? null : scenario);
    const next = applyBasePreset(inputs, retentionPresets, preset);
    setInputs(next.inputs);
    setRetentionPresets(next.state);
  };
  const handleToggleOnTarget = () => handleBasePreset('onTarget');
  const handleToggleLowRetention = () => handleBasePreset('low');
  const handlePlusFive = () => {
    setPackageScenario(scenario => scenario === 'plusFive' ? null : scenario);
    // +5% only lifts retention — sales performance is left untouched.
    const next = togglePlusFive(inputs, retentionPresets);
    setInputs(next.inputs);
    setRetentionPresets(next.state);
  };
  // Typing a custom retention value drops any preset selection.
  useEffect(() => {
    const next = reconcilePresets(inputs, retentionPresets);
    if (next !== retentionPresets) setRetentionPresets(next);
  }, [inputs.pctLaunched, inputs.pct30, inputs.pct90, inputs.pct1y]);
  const handleOptimizeForTarget = (target: number, months: Timeframe = timeframe) => {
    if (!target || target <= 0) return;
    const stash = inputs.monthlyCampaigns && inputs.monthlyCampaigns.length === 12 ? [...inputs.monthlyCampaigns] : Array(12).fill(inputs.campaigns);
    const pattern = optimizeCampaignsForRevenueTarget(inputs, target, months);
    if (pattern) {
      setPreOptimizeMonthlyCampaigns(stash);
      setInputs({
        ...inputs,
        monthlyCampaigns: pattern,
        campaigns: pattern[0],
        revenueTarget: target
      });
    }
  };
  const handleOptimizeForTeamTarget = (target: number, months: Timeframe = timeframe) => {
    if (!target || target <= 0) return;
    const stash = inputs.monthlyCampaigns && inputs.monthlyCampaigns.length === 12 ? [...inputs.monthlyCampaigns] : Array(12).fill(inputs.campaigns);
    const pattern = optimizeCampaignsForTeamTarget(inputs, target, months);
    if (pattern) {
      setPreOptimizeMonthlyCampaigns(stash);
      setInputs({
        ...inputs,
        monthlyCampaigns: pattern,
        campaigns: pattern[0]
      });
    }
  };
  const handleUndoOptimize = () => {
    if (preOptimizeMonthlyCampaigns) {
      setInputs({
        ...inputs,
        monthlyCampaigns: preOptimizeMonthlyCampaigns,
        campaigns: preOptimizeMonthlyCampaigns[0]
      });
      setPreOptimizeMonthlyCampaigns(null);
    }
  };
  // Handle numeric input changes for Review section
  const handleNumericChange = (field: keyof Inputs, value: string) => {
    if (value === '') {
      setInputs({
        ...inputs,
        [field]: ''
      });
      return;
    }
    const numValue = parseFloat(value);
    if (!isNaN(numValue)) {
      setInputs({
        ...inputs,
        [field]: numValue
      });
    }
  };
  const setMonthlyAdSpendTotal = (total: number) => {
    // Any custom total is allowed. The campaign tier is derived from $2,500
    // bands: $1-2,500 = 1x, $2,501-5,000 = 2x, $5,001-7,500 = 3x, and so on.
    const normalizedTotal = Math.max(1, Math.round(total));
    const campaignCount = Math.ceil(normalizedTotal / CAMPAIGN_SPEND_STEP);
    const spendPerCampaign = normalizedTotal / campaignCount;
    setReviewSpendInput('');
    setInputs(currentInputs => ({
      ...currentInputs,
      campaigns: campaignCount,
      monthlyCampaigns: Array(12).fill(campaignCount),
      adSpend: spendPerCampaign,
      monthlyAdSpend: Array(12).fill(spendPerCampaign)
    }));
  };
  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopyLinkLabel('Link Copied✓');
      setTimeout(() => setCopyLinkLabel('Copy Share Link'), 1200);
    } catch (err) {
      console.error('Failed to copy link', err);
    }
  };
  const handleSavePdf = async () => {
    setSavePdfLabel('Generating PDF...');
    try {
      const html2canvas = (await import('html2canvas')).default;
      const jsPDF = (await import('jspdf')).default;
      const element = resultsRef.current;
      if (!element) return;
      const canvas = await html2canvas(element, {
        scale: 2,
        useCORS: true,
        logging: false,
        backgroundColor: '#ffffff'
      });
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4'
      });
      const imgWidth = 210;
      const pageHeight = 297;
      const imgHeight = canvas.height * imgWidth / canvas.width;
      let heightLeft = imgHeight;
      let position = 0;
      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;
      while (heightLeft > 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }
      const companyName = inputs.companyName || 'Company';
      const fileName = `${companyName.replace(/\s+/g, '_')}_ROI_Projection.pdf`;
      pdf.save(fileName);
      setSavePdfLabel('PDF Saved ✓');
      setTimeout(() => setSavePdfLabel('Save as PDF'), 2000);
    } catch (err) {
      console.error('Failed to generate PDF', err);
      setSavePdfLabel('Failed - Try Again');
      setTimeout(() => setSavePdfLabel('Save as PDF'), 2000);
    }
  };
  // Calculated values - REVERSED RETENTION FUNNEL
  // Start with 1 rep at Active 1 Year and calculate upward
  const targetActive1Year = 1; // Fixed: 1 rep who stays long-term
  // Work backwards through retention rates to find how many hires needed
  const reverseActive90 = targetActive1Year / (inputs.pct1y / 100);
  const reverseActive30 = reverseActive90 / (inputs.pct90 / 100);
  const reverseLaunched = reverseActive30 / (inputs.pct30 / 100);
  const reverseHires = reverseLaunched / (inputs.pctLaunched / 100);
  // Overhead derives straight from the funnel: 30 min per scheduled interview,
  // 1 hour per interview + 2 hours per hire, priced at admin vs leadership rates.
  const overhead = computeOverhead(reverseHires, funnelRates);
  // Keep the original forward calculations for other uses
  const adSpendTotal = inputs.adSpend * inputs.campaigns;
  const estimatedHires = Math.round(adSpendTotal / inputs.cph);
  // The cost-per-hire band around the median that drives the projections. Each
  // end can be edited directly; the median is always the midpoint.
  const cphRange = {
    low: inputs.cphLow ?? Math.max(1, inputs.cph - CPH_RANGE_SPREAD),
    high: inputs.cphHigh ?? inputs.cph + CPH_RANGE_SPREAD
  };
  const setCphRange = (low: number, high: number) => {
    const safeLow = Math.max(1, Math.round(low));
    const safeHigh = Math.max(safeLow, Math.round(high));
    setInputs(prev => ({
      ...prev,
      cphLow: safeLow,
      cphHigh: safeHigh,
      cph: Math.round((safeLow + safeHigh) / 2)
    }));
  };
  // While typing, the raw text is held locally so the field can be cleared and
  // retyped without being clamped on every keystroke.
  const commitCphDrafts = () => {
    const parsedLow = parseFloat(cphLowDraft ?? '');
    const parsedHigh = parseFloat(cphHighDraft ?? '');
    const nextLow = Number.isNaN(parsedLow) ? cphRange.low : parsedLow;
    const nextHigh = Number.isNaN(parsedHigh) ? cphRange.high : parsedHigh;
    setCphLowDraft(null);
    setCphHighDraft(null);
    setCphRange(nextLow, nextHigh);
  };
  const shiftCphRange = (delta: number) => {
    if (delta < 0 && cphRange.low + delta < 1) return;
    setCphRange(cphRange.low + delta, cphRange.high + delta);
  };
  // Hires at the top of the cost band (fewest) through the bottom (most).
  const hiresRange = {
    low: Math.round(adSpendTotal / cphRange.high),
    high: Math.round(adSpendTotal / cphRange.low)
  };
  // Changes to monthly ad spend or the cost-per-hire range (and so the
  // expected monthly hires) carry over to the Partnership page. Only actual
  // changes are sent, so opening the calculator never overwrites values that
  // were set on the Partnership page.
  const {
    setAdSpend: setPartnershipAdSpend,
    setCpaLow: setPartnershipCpaLow,
    setCpaHigh: setPartnershipCpaHigh,
    setCompanyName: setBriefCompanyName,
    setCalculatorInputs,
    setCalculatorScenario,
    calculatorInputs: sharedCalculatorInputs
  } = usePricing();
  // The Financial Model PDF reads the calculator's inputs and scenarios. The
  // untouched defaults aren't sent, so the model keeps following the deck's
  // settings until the calculator is actually changed.
  useEffect(() => {
    setCalculatorInputs(inputs === DEFAULT_INPUTS ? null : inputs);
  }, [inputs, setCalculatorInputs]);
  // Assumptions edited in the proposal's Tailor panel land in the shared
  // inputs; adopt them so the calculator shows the same numbers.
  useEffect(() => {
    if (sharedCalculatorInputs && sharedCalculatorInputs !== inputs) {
      setInputs(sharedCalculatorInputs);
    }
    // Only react to outside changes, not the calculator's own edits.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sharedCalculatorInputs]);
  useEffect(() => {
    setCalculatorScenario({
      halfSales,
      doubleAttrition
    });
  }, [halfSales, doubleAttrition, setCalculatorScenario]);
  // The company name entered here also labels the Partnership Brief
  // ("Prepared for …"). An empty name on first load is not sent, so it never
  // wipes a name typed straight into the brief.
  const lastCompanyRef = useRef<string | null>(null);
  useEffect(() => {
    const name = inputs.companyName ?? '';
    const prev = lastCompanyRef.current;
    lastCompanyRef.current = name;
    if (prev === null ? name !== '' : name !== prev) {
      setBriefCompanyName(name);
    }
  }, [inputs.companyName, setBriefCompanyName]);
  const lastSyncedRef = useRef<{
    spend: number;
    low: number;
    high: number;
  } | null>(null);
  useEffect(() => {
    const next = {
      spend: adSpendTotal,
      low: cphRange.low,
      high: cphRange.high
    };
    const prev = lastSyncedRef.current;
    lastSyncedRef.current = next;
    if (!prev) return;
    if (next.spend !== prev.spend && Number.isFinite(next.spend) && next.spend > 0) {
      setPartnershipAdSpend(Math.round(next.spend));
    }
    if ((next.low !== prev.low || next.high !== prev.high) && Number.isFinite(next.low) && Number.isFinite(next.high) && next.low > 0) {
      setPartnershipCpaLow(next.low);
      setPartnershipCpaHigh(Math.max(next.low, next.high));
    }
  }, [adSpendTotal, cphRange.low, cphRange.high, setPartnershipAdSpend, setPartnershipCpaLow, setPartnershipCpaHigh]);
  const estimatedLaunched = estimatedHires * (inputs.pctLaunched / 100);
  const estimatedActive30 = estimatedLaunched * (inputs.pct30 / 100);
  const estimatedActive90 = estimatedActive30 * (inputs.pct90 / 100);
  const estimatedActive1Year = estimatedActive90 * (inputs.pct1y / 100);
  const gpPerSale = inputs.ticket * (inputs.gpm / 100);
  const rookieTotalSales = salesPerNinetyDayRep(inputs);
  // Round rep counts to nearest tenth for clean display
  const roundedActive90 = Math.round(estimatedActive90 * 10) / 10;
  const roundedActive1Year = Math.round(estimatedActive1Year * 10) / 10;
  const rookieMonthlyRevenue = roundedActive90 * rookieTotalSales * inputs.ticket * (inputs.pullthrough / 100) / 3;
  const veteranMonthlyRevenue = roundedActive1Year * inputs.salesVetMonthly * inputs.ticket * (inputs.pullthrough / 100);
  const canCalculate = true; // Allow calculation even without package selected
  // Show loading during initial mount to prevent hydration mismatch
  if (!isClient) {
    return <div className="d2d-theme min-h-screen" data-id="element-1946" data-mp="3t8sm0hrl7m7n"></div>;
  }
  // Main calculator content
  return <div className={`d2d-theme min-h-screen ${isLightMode ? 'light-mode' : ''}`} data-id="element-1947" data-mp="ttoyyzz1np15">
      {/* Floating Edit Button - Always visible */}
      <button onClick={() => setIsSidebarOpen(true)} className="fixed bottom-6 right-6 z-30 w-14 h-14 rounded-full bg-amber hover:bg-amber-dark text-navy shadow-2xl flex items-center justify-center transition-all hover:scale-110 active:scale-95" title="Edit Assumptions" data-id="element-1948" data-mp="ptevbtyq5apg">
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-1949" data-mp="35k120do5ia5y">
          <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" data-id="element-1950" data-mp="2qedinxfkag1a" />
          <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" data-id="element-1951" data-mp="2vd6ullf79yzc" />
        </svg>
      </button>

      {/* Assumptions Sidebar */}
      <AssumptionsSidebar isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} inputs={inputs} onChange={setInputs} data-id="element-1952" data-mp="355mdqm28gtlt" />

      {/* Header */}
      <header className="border-b border-white/10 bg-navy/95" data-id="element-1953" data-mp="3e5ive3q7gs7d">
        <div className="max-w-[1440px] mx-auto px-5 md:px-8 py-4 flex items-center justify-between gap-6" data-id="element-1954" data-mp="3e3y2eijjhhbm">
          <div className="flex items-center gap-4 shrink-0" data-id="element-1955" data-mp="3j2sru3c0i44p">
            <div className={`rounded-md px-3 py-2 shadow-sm ${isLightMode ? 'bg-white' : 'bg-navy'}`} aria-label="D2D HIRE" data-id="element-1956" data-mp="3o2x8btcx6yux">
              <img src={isLightMode ? 'https://cdn.magicpatterns.com/uploads/agUMR24bt7hzCEPWvymd5Y/Full_Multi_color.png' : 'https://cdn.magicpatterns.com/uploads/dakpUsyM83GcxWqCq39SEW/HIRE_White_Logo.png'} alt="D2D HIRE" className="h-7 md:h-8" data-id="element-1957" data-mp="1tpnvpsp2ux8g" />
            </div>
            <span className="hidden sm:block pl-4 border-l border-white/15 text-lg md:text-2xl font-bold tracking-[-0.02em] text-white" data-id="element-1958" data-mp="48a92k39390w">
              High Impact Recruiting Engine
            </span>
          </div>
          <div className="flex items-center gap-3 shrink-0" data-id="element-1959" data-mp="37qc1kg025ij1">
            <span className="hidden lg:flex items-center gap-2 text-xs font-semibold text-slate-300" data-id="element-1960" data-mp="37rvcrs6ux3dy">
              <span className="w-1.5 h-1.5 rounded-full bg-amber" data-id="element-1961" data-mp="3hnq2t8deu0qr" />
              Built for D2D teams
            </span>
            <button type="button" onClick={() => setIsLightMode(currentMode => !currentMode)} className="inline-flex items-center gap-2 rounded-md border border-white/20 bg-white/10 px-3 py-2 text-xs font-bold text-white transition-colors hover:bg-white/20 focus:outline-none focus-visible:ring-2 focus-visible:ring-amber focus-visible:ring-offset-2 focus-visible:ring-offset-navy" aria-pressed={isLightMode} aria-label={isLightMode ? 'Switch to dark mode' : 'Switch to light mode'} data-id="element-1962" data-mp="10yjiwy9wc14h">
              <span aria-hidden="true" className="text-sm leading-none" data-id="element-1963" data-mp="zjql1u82saxt">
                {isLightMode ? '◐' : '☀'}
              </span>
              {isLightMode ? 'Dark mode' : 'Light mode'}
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-[1440px] mx-auto px-5 md:px-8 py-10 md:py-16 pb-20" data-id="element-1964" data-mp="1dgx1hi5btxjv">
        {/* Hero Section */}
        <div className="mb-12 md:mb-16 text-center max-w-4xl mx-auto" data-id="element-1965" data-mp="1if6zs1mcle9p">
          <h1 className="hero-title text-4xl md:text-5xl lg:text-6xl font-medium text-white leading-[1.08] tracking-[-0.045em]" data-id="element-1966" data-mp="2663ww89hi70w">
            Predictable recruiting,
            <br data-id="element-1967" data-mp="3cx34fnmgfxdc" />
            profitable growth.
          </h1>
          <div className="inline-flex items-center gap-2 mt-5 text-[11px] font-extrabold tracking-[0.16em] uppercase text-amber" data-id="element-1968" data-mp="3cwjw8ymlwnci">
            <span className="w-1.5 h-1.5 rounded-full bg-amber" data-id="element-1969" data-mp="3mmwh6ryc2ij6" />
            Recruiting forecast for D2D sales leaders
          </div>
        </div>

        {/* Step 1: Input Form */}
        <div className="mb-8 md:mb-12" data-id="element-1970" data-mp="3ro29ioo303s6">
          <div className="flex items-center gap-3 mb-4 md:mb-6" data-id="element-1971" data-mp="2tyqtpxfwaffn">
            <div className="w-10 h-10 rounded bg-amber flex items-center justify-center text-lg font-bold text-navy" data-id="element-1972" data-mp="2u0ljpq60wxql">
              1
            </div>
            <h2 className="text-xl md:text-2xl font-bold text-navy" data-id="element-1973" data-mp="33vddeiwhj0g3">
              Business Assumptions
            </h2>
          </div>
          <CalculatorInputs inputs={inputs} onChange={setInputs} onReset={handleReset} onReview={handleReview} showActionButtons={false} data-id="element-1974" data-mp="38tez7blapdv9" />
        </div>

        {/* Step 2: Validation Summary */}
        {hasReviewed && <div ref={reviewRef} className="mb-8 md:mb-12" data-id="element-1975" data-mp="28mib2jy79irh">
            <div className="flex items-center gap-3 mb-4 md:mb-6" data-id="element-1976" data-mp="23nymscw4vuvy">
              <div className="w-10 h-10 rounded bg-amber flex items-center justify-center text-lg font-bold text-navy" data-id="element-1977" data-mp="1yxi3wd9jnh27">
                2
              </div>
              <h2 className="text-xl md:text-2xl font-bold text-navy" data-id="element-1978" data-mp="1txvru75gc6uz">
                Review Your Numbers
              </h2>
            </div>
            <div className="bg-white border border-gray-200 rounded-xl p-5 md:p-7 shadow-lg" data-id="element-1979" data-mp="1kxyyvtc14lu4">
              {/* Hiring Funnel Breakdown Button */}
              <div className="mb-6 flex justify-end gap-3" data-id="element-1980" data-mp="1fz5wox270h99">
                <button onClick={handleToggleLowRetention} aria-pressed={lowRetention} className={`px-3 py-2 rounded text-xs font-semibold transition-colors border-2 ${lowRetention ? 'bg-gray-700 text-white border-gray-800' : 'bg-white text-gray-700 border-gray-300 hover:border-gray-500'}`} data-id="element-1981" data-mp="1b02pb5yqpyy4">
                  {lowRetention ? '✓ ' : ''}Low
                </button>
                <button onClick={handleToggleOnTarget} aria-pressed={onTarget} className={`px-3 py-2 rounded text-xs font-semibold transition-colors border-2 ${onTarget ? 'bg-emerald-500 text-white border-emerald-600' : 'bg-white text-gray-700 border-gray-300 hover:border-emerald-400'}`} data-id="element-1982" data-mp="18go77hdx8run">
                  {onTarget ? '✓ ' : ''}On Target
                </button>
                <button onClick={handlePlusFive} aria-pressed={plusFive} className={`px-3 py-2 rounded text-xs font-semibold transition-colors border-2 ${plusFive ? 'bg-blue-500 text-white border-blue-600' : 'bg-white text-gray-700 border-gray-300 hover:border-blue-400 hover:text-blue-600'} active:translate-y-[1px]`} data-id="element-1983" data-mp="1rbmq2t9g1iz5">
                  {plusFive ? '✓ ' : ''}+5%
                </button>
              </div>

              {/* Monthly Ad Investment & Retention Funnel - Side by Side */}
              <div className="mb-8 grid grid-cols-1 lg:grid-cols-[300px_1fr] gap-6" data-id="element-1984" data-mp="2f8j1619l1ttb">
                {/* Left: Monthly Ad Investment */}
                <div data-id="element-1985" data-mp="2t6r3kope1nbv"></div>

                {/* Right: Retention Funnel Visualization */}
              </div>

              {/* Expandable Hiring Funnel - Conditionally shown */}
              <div className="mb-4" data-id="element-1986" data-mp="21i5rnuh1m71v">
                {/* Overhead Section - appears ABOVE Recruiting Metrics */}
                {showOverhead && <div className="mb-8" data-id="element-1987" data-mp="2aarbldweujsa">
                    <div className="relative group" data-id="element-1988" data-mp="2abitxn0zyl0m">
                      <h4 className="text-lg font-bold text-navy mb-4 border-b border-gray-200 pb-2" data-id="element-1989" data-mp="25izfknuhjlur">
                        Overhead
                        <button onClick={e => {
                    e.stopPropagation();
                    setShowOverhead(false);
                  }} className="ml-3 inline-flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity duration-200 text-xs font-bold text-gray-400 hover:text-amber-500 uppercase tracking-wide align-middle" aria-label="Collapse Overhead" data-id="element-1990" data-mp="1hsdjzw1o3u1n">
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-1991" data-mp="258jo8nolviwe">
                            <polyline points="6 9 12 15 18 9" data-id="element-1992" data-mp="os71al55asp8" />
                          </svg>
                          Collapse
                        </button>
                      </h4>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-1993" data-mp="m7iugxzd5prs">
                      {/* Total Time Investment */}
                      <div className="p-4 rounded bg-orange-50 border-2 border-orange-200" data-id="element-1994" data-mp="hg0zcqk0ujeq">
                        <div className="text-sm font-bold text-navy mb-3" data-id="element-1995" data-mp="cj0wspmdrysd">
                          Total Time Investment
                        </div>
                        <div className="space-y-3" data-id="element-1996" data-mp="7h1rvbazsttb">
                          <div className="p-2 rounded" data-id="element-1997" data-mp="155bv1tmilnm0">
                            <div className="text-xs text-gray-600 mb-1 font-semibold uppercase tracking-wide" data-id="element-1998" data-mp="107k16u2b7rf6">
                              Pre-Interview
                            </div>
                            <div className="flex items-baseline gap-1" data-id="element-1999" data-mp="vaa36q0uu7fq">
                              <span className="text-xl font-bold text-navy" data-id="element-2000" data-mp="q94csyuo2w9d">
                                {formatHours(overhead.preInterviewHours)}
                              </span>
                              <span className="text-gray-500 font-bold text-sm" data-id="element-2001" data-mp="3ay1tbtae2683">
                                hours
                              </span>
                            </div>
                            <div className="mt-1 text-xs text-gray-600" data-id="element-2002" data-mp="270g9pse73y0b">
                              30 min per scheduled interview + 15 min per
                              screened applicant who never scheduled
                            </div>
                          </div>
                          <div className="p-2 rounded" data-id="element-2003" data-mp="1jbwzv8fhzd64">
                            <div className="text-xs text-gray-600 mb-1 font-semibold uppercase tracking-wide" data-id="element-2004" data-mp="1jac5pjx3hw8s">
                              Interview-to-Onboarding
                            </div>
                            <div className="flex items-baseline gap-1" data-id="element-2005" data-mp="1a9nqwmzvnruu">
                              <span className="text-xl font-bold text-navy" data-id="element-2006" data-mp="1fb3b6cu8duc6">
                                {formatHours(overhead.interviewToOnboardingHours)}
                              </span>
                              <span className="text-gray-500 font-bold text-sm" data-id="element-2007" data-mp="lpu5mmfozl86">
                                hours
                              </span>
                            </div>
                            <div className="mt-1 text-xs text-gray-600" data-id="element-2008" data-mp="vpb9qzfapr21">
                              1 hr per interview + 2 hrs per hire
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Money Value of Time */}
                      <div className="p-4 rounded bg-red-50 border-2 border-red-200" data-id="element-2009" data-mp="10oo5k9o5a8gr">
                        <div className="text-sm font-bold text-navy mb-3" data-id="element-2010" data-mp="1efszqw8qu8jp">
                          Money Value of Time
                        </div>
                        <div className="space-y-3" data-id="element-2011" data-mp="19gygxuas0jzl">
                          <div className="p-2 rounded" data-id="element-2012" data-mp="1ocxkqsqurxyx">
                            <div className="text-xs text-gray-600 mb-1 font-semibold uppercase tracking-wide" data-id="element-2013" data-mp="1obcv9hjeb57x">
                              Admin
                            </div>
                            <div className="text-xl font-bold text-navy" data-id="element-2014" data-mp="41igc61rm58a">
                              ${Math.round(overhead.adminCost).toLocaleString()}
                            </div>
                            <div className="mt-1 text-xs text-gray-600" data-id="element-2015" data-mp="e1ixblc5w99b">
                              Pre-interview time at ${ADMIN_HOURLY_RATE}/hour
                            </div>
                          </div>
                          <div className="p-2 rounded" data-id="element-2016" data-mp="3hire01v5l32g">
                            <div className="text-xs text-gray-600 mb-1 font-semibold uppercase tracking-wide" data-id="element-2017" data-mp="3mf84h2ptrtip">
                              Leadership
                            </div>
                            <div className="text-xl font-bold text-navy" data-id="element-2018" data-mp="yguvtpypiz">
                              $
                              {Math.round(overhead.leadershipCost).toLocaleString()}
                            </div>
                            <div className="mt-1 text-xs text-gray-600" data-id="element-2019" data-mp="1ku5earncu0mn">
                              Interview-to-onboarding time at $
                              {LEADERSHIP_HOURLY_RATE}/hour
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>}

                <div className="relative group" data-id="element-2020" data-mp="16x09euzdn7sz">
                  {/* Hidden arrow above Recruiting Metrics heading - shows when funnel is fully expanded */}
                  {showHiringMetrics && !showOverhead && <button onClick={() => setShowOverhead(true)} className="absolute -top-6 left-0 w-full flex items-center gap-3 opacity-0 group-hover:opacity-100 transition-opacity duration-200 cursor-pointer z-10" aria-label="Show Overhead" data-id="element-2021" data-mp="t2zigneo2p36">
                      <div className="w-20 text-right flex justify-end pr-4" data-id="element-2022" data-mp="e79von1nq7xe">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#F59E0B" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="animate-bounce" data-id="element-2023" data-mp="3gyan7d8c574f">
                          <polyline points="18 15 12 9 6 15" data-id="element-2024" data-mp="3pelbdjdrsi9" />
                        </svg>
                      </div>
                      <div className="text-xs font-bold text-amber-500 uppercase tracking-wide" data-id="element-2025" data-mp="3pyp9aet4iq8z">
                        Click to expand
                      </div>
                    </button>}

                  <h3 className="text-lg font-bold text-navy mb-4 border-b border-gray-200 pb-2" data-id="element-2026" data-mp="2ejabx9x05wdc">
                    Recruiting Metrics
                    {/* Collapse button for both Overhead + Recruiting Metrics */}
                    {showHiringMetrics && <button onClick={e => {
                  e.stopPropagation();
                  setShowHiringMetrics(false);
                  setShowOverhead(false);
                }} className="ml-3 inline-flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity duration-200 text-xs font-bold text-gray-400 hover:text-amber-500 uppercase tracking-wide align-middle" aria-label="Collapse Recruiting Metrics" data-id="element-2027" data-mp="pxtje0t3lz0y">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-2028" data-mp="1hsdps0klsq2y">
                          <polyline points="6 9 12 15 18 9" data-id="element-2029" data-mp="vnutupw1zgbg" />
                        </svg>
                        Collapse
                      </button>}
                  </h3>
                </div>

                {showHiringMetrics && <div className="mb-6" data-id="element-2030" data-mp="zowxrk0x0gjr">
                    <ExpandableHiringFunnel estimatedHires={reverseHires} rates={funnelRates} onRatesChange={setFunnelRates} data-id="element-2031" data-mp="zn3mfrfcx85f" />
                  </div>}
              </div>

              {/* Projected Recruiting Metrics */}
              <div className="mb-8" data-id="element-2032" data-mp="1ngbyx9gygrtf">
                <div className="relative group" data-id="element-2033" data-mp="1jc4brgq5bjxb">
                  {/* Hidden arrow above Retention Metrics heading */}
                  {!showHiringMetrics && <button onClick={() => setShowHiringMetrics(true)} className="absolute -top-6 left-0 w-full flex items-center gap-3 opacity-0 group-hover:opacity-100 transition-opacity duration-200 cursor-pointer z-10" aria-label="Show Recruiting Metrics" data-id="element-2034" data-mp="1xa2lu9xfou70">
                      <div className="w-20 text-right flex justify-end pr-4" data-id="element-2035" data-mp="21zwnlv38lb06">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#F59E0B" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="animate-bounce" data-id="element-2036" data-mp="26zhxv1nux5s7">
                          <polyline points="18 15 12 9 6 15" data-id="element-2037" data-mp="1o9t7zmyv4fjp" />
                        </svg>
                      </div>
                      <div className="text-xs font-bold text-amber-500 uppercase tracking-wide" data-id="element-2038" data-mp="1ac4lybt272d2">
                        Recruiting Metrics
                      </div>
                    </button>}

                  <h3 className="text-lg font-bold text-navy mb-4 border-b border-gray-200 pb-2" data-id="element-2039" data-mp="129cf9s6sqzh7">
                    Retention Metrics
                  </h3>
                </div>

                <div className="space-y-3" data-id="element-2040" data-mp="xfzqni24asog">
                  <div className="flex items-center gap-3" data-id="element-2041" data-mp="sk1665jwtutb">
                    <div className="w-20 text-right" data-id="element-2042" data-mp="oho4bs8aah1t">
                      <span className="text-xl font-bold text-navy" data-id="element-2043" data-mp="oifnu4olwobq">
                        {reverseHires.toFixed(1)}
                      </span>
                    </div>
                    <div className="w-28 text-base font-semibold text-gray-700" data-id="element-2044" data-mp="eiosy1w4w7b4">
                      Hired
                    </div>
                    <div className="flex-1 bg-white/10 rounded-full h-8 relative overflow-hidden border border-white/10" data-id="element-2045" data-mp="9lyetk3w3lsc">
                      <div className="absolute inset-y-0 left-0 bg-amber rounded-full" style={{
                    width: '100%'
                  }} data-id="element-2046" data-mp="28v585s6wqvs4" />
                    </div>
                  </div>

                  <div className="flex items-center gap-3" data-id="element-2047" data-mp="2gf7ig5roypre">
                    <div className="w-20 text-right" data-id="element-2048" data-mp="2l7h4vbtll7ox">
                      <span className="text-lg font-bold text-navy" data-id="element-2049" data-mp="2la3a34t0nb3z">
                        {reverseLaunched.toFixed(1)}
                      </span>
                    </div>
                    <div className="w-28 text-base font-semibold text-gray-700" data-id="element-2050" data-mp="1tidwpkylbd70">
                      Launched
                    </div>
                    <div className="flex-1 bg-gray-200 rounded-full h-8 relative overflow-hidden" data-id="element-2051" data-mp="22hiuuq68p4in">
                      <div className="absolute inset-y-0 left-0 bg-gradient-to-r from-amber to-amber/90 rounded-full" style={{
                    width: `${reverseLaunched / reverseHires * 100}%`
                  }} data-id="element-2052" data-mp="27g2iqw4e8qdn" />
                    </div>
                    <div className="w-16 text-sm font-semibold text-gray-600" data-id="element-2053" data-mp="2t6c1grejqd6p">
                      {inputs.pctLaunched}%
                    </div>
                  </div>

                  <div className="flex items-center gap-3" data-id="element-2054" data-mp="3359kxfx47giu">
                    <div className="w-20 text-right" data-id="element-2055" data-mp="334rpumm6v8ci">
                      <span className="text-lg font-bold text-navy" data-id="element-2056" data-mp="2y66t4x65b6st">
                        {reverseActive30.toFixed(1)}
                      </span>
                    </div>
                    <div className="w-28 text-base font-semibold text-gray-700" data-id="element-2057" data-mp="25i15o2npj3pa">
                      Active 30d
                    </div>
                    <div className="flex-1 bg-gray-200 rounded-full h-8 relative overflow-hidden" data-id="element-2058" data-mp="2k7gv9fbi533e">
                      <div className="absolute inset-y-0 left-0 bg-gradient-to-r from-blue-500 to-blue-400 rounded-full" style={{
                    width: `${reverseActive30 / reverseHires * 100}%`
                  }} data-id="element-2059" data-mp="2ff6wrawbmupi" />
                    </div>
                    <div className="w-16 text-sm font-semibold text-gray-600" data-id="element-2060" data-mp="275yp71ddyqn">
                      {inputs.pct30}%
                    </div>
                  </div>

                  <div className="flex items-center gap-3" data-id="element-2061" data-mp="3ohrzexmjdekm">
                    <div className="w-20 text-right" data-id="element-2062" data-mp="3tfab2vrxu7wb">
                      <span className="text-lg font-bold text-navy" data-id="element-2063" data-mp="3tftktp7fppz9">
                        {reverseActive90.toFixed(1)}
                      </span>
                    </div>
                    <div className="w-28 text-base font-semibold text-gray-700" data-id="element-2064" data-mp="pxief42uwzrs">
                      Active 90d
                    </div>
                    <div className="flex-1 bg-gray-200 rounded-full h-8 relative overflow-hidden" data-id="element-2065" data-mp="c1enpkr6z9k2">
                      <div className="absolute inset-y-0 left-0 bg-gradient-to-r from-green-500 to-green-400 rounded-full" style={{
                    width: `${reverseActive90 / reverseHires * 100}%`
                  }} data-id="element-2066" data-mp="g2i2s51f11k8" />
                    </div>
                    <div className="w-16 text-sm font-semibold text-gray-600" data-id="element-2067" data-mp="3neutrawy4dzw">
                      {inputs.pct90}%
                    </div>
                  </div>

                  <div className="flex items-center gap-3" data-id="element-2068" data-mp="3jfm1kahk0o6g">
                    <div className="w-20 text-right" data-id="element-2069" data-mp="3efznjytwj3wl">
                      <span className="text-lg font-bold text-navy" data-id="element-2070" data-mp="ezb5yrf1nns8">
                        {targetActive1Year.toFixed(1)}
                      </span>
                    </div>
                    <div className="w-28 text-base font-semibold text-gray-700" data-id="element-2071" data-mp="b0bu0muwaqra">
                      Active 1 Year
                    </div>
                    <div className="flex-1 bg-gray-200 rounded-full h-8 relative overflow-hidden" data-id="element-2072" data-mp="18xysr9z8d3y">
                      <div className="absolute inset-y-0 left-0 bg-gradient-to-r from-purple-500 to-purple-400 rounded-full" style={{
                    width: `${targetActive1Year / reverseHires * 100}%`
                  }} data-id="element-2073" data-mp="ynsfb5jqtl8q" />
                    </div>
                    <div className="w-16 text-sm font-semibold text-gray-600" data-id="element-2074" data-mp="167baea69h4xo">
                      {inputs.pct1y}%
                    </div>
                  </div>
                </div>
              </div>

              {/* Sales Performance and Deal Economics - Side by Side */}
              <div className="mb-8" data-id="element-2075" data-mp="1k6b25qp2lzy2">
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8" data-id="element-2076" data-mp="1k4zv9i0bwc2q">
                  {/* Left Column: Monthly Ad Spend */}
                  <div data-id="element-2077" data-mp="1tw44e13bp2pn">
                    <div className="group relative h-full p-4 md:p-6 rounded bg-gray-50 border-2 border-gray-200 flex flex-col justify-center transition-colors hover:border-amber" style={{
                  minHeight: '120px'
                }} data-id="element-2078" data-mp="1txylf4bh4aog">
                      <div className="text-xs text-gray-600 mb-2 font-semibold uppercase tracking-wide" data-id="element-2079" data-mp="27uc4uizkim5k">
                        Monthly Ad Spend
                      </div>
                      <div className="relative" data-id="element-2080" data-mp="3enxhlrbyifx2">
                        <span className="absolute left-0 top-1/2 -translate-y-1/2 text-gray-500 font-bold text-xl" data-id="element-2081" data-mp="30nd1j2xfey79">
                          $
                        </span>
                        <input type="number" min={CAMPAIGN_SPEND_STEP} step={CAMPAIGN_SPEND_STEP} value={reviewSpendInput || adSpendTotal} onChange={e => setReviewSpendInput(e.target.value)} onBlur={() => {
                      if (reviewSpendInput) {
                        setMonthlyAdSpendTotal(parseFloat(reviewSpendInput));
                      }
                    }} onKeyDown={e => {
                      if (e.key === 'Enter') {
                        e.currentTarget.blur();
                      }
                    }} className="no-spinner inline-field w-full pl-6 pr-16 text-2xl md:text-3xl font-bold text-navy bg-transparent border-none outline-none focus:text-amber transition-colors" aria-label="Monthly ad spend" data-id="element-2082" data-mp="35ok7cwg0dnba" />
                        <div className="absolute right-0 top-1/2 -translate-y-1/2 flex items-center gap-1 opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 transition-opacity duration-200" data-id="element-2083" data-mp="1mn03udb9sc3q">
                        <button type="button" onClick={() => setMonthlyAdSpendTotal(adSpendTotal + CAMPAIGN_SPEND_STEP)} className="flex h-6 w-6 items-center justify-center rounded-full bg-navy-light text-xs font-bold text-white shadow-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-amber" aria-label="Increase monthly ad spend by $2,500" data-id="element-2084" data-mp="1voq488n4qldn">
                          ↑
                        </button>
                        {adSpendTotal > CAMPAIGN_SPEND_STEP && <button type="button" onClick={() => setMonthlyAdSpendTotal(adSpendTotal - CAMPAIGN_SPEND_STEP)} className="flex h-6 w-6 items-center justify-center rounded-full bg-navy-light text-xs font-bold text-white shadow-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-amber" aria-label="Decrease monthly ad spend by $2,500" data-id="element-2085" data-mp="2dqdi6otvl8et">
                            ↓
                          </button>}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Middle Column: Average Ad Spend Cost per Hire */}
                  <div data-id="element-2086" data-mp="ksq90pv2tgfk">
                    <div className="group relative h-full p-4 md:p-6 rounded bg-blue-50 border-2 border-blue-200 flex flex-col justify-center hover:border-amber transition-colors" style={{
                  minHeight: '120px'
                }} data-id="element-2087" data-mp="kw4foaarwbst">
                      <div className="text-xs text-gray-600 mb-2 font-semibold uppercase tracking-wide" data-id="element-2088" data-mp="23jrv79xkwvv">
                        Ad Spend Cost per Hire Range
                      </div>
                      <div className="relative" data-id="element-2089" data-mp="3t80abvr56hma">
                        <div className="flex items-center pr-16 text-2xl md:text-3xl font-bold text-navy" data-id="element-2090" data-mp="3p77zwz6ja6of">
                          <span className="text-gray-500 text-xl" data-id="element-2091" data-mp="3kahia7f2z30w">$</span>
                          <input type="number" min="1" step={CPH_STEP} value={cphLowDraft ?? cphRange.low} onChange={e => setCphLowDraft(e.target.value)} onBlur={commitCphDrafts} onKeyDown={e => {
                        if (e.key === 'Enter') e.currentTarget.blur();
                      }} aria-label="Low end of ad spend cost per hire" style={{
                        width: `${tightFieldWidthCh(cphLowDraft ?? cphRange.low)}ch`
                      }} className="no-spinner inline-field text-right text-2xl md:text-3xl font-bold text-navy bg-transparent border-none outline-none focus:text-amber transition-colors" data-id="element-2092" data-mp="1o5rz19x1jhwp" />
                          <span className="text-gray-400 mx-1" data-id="element-2093" data-mp="2usdz2c00w5qa">&ndash;</span>
                          <span className="text-gray-500 text-xl" data-id="element-2094" data-mp="34yx27hn9t1v4">$</span>
                          <input type="number" min="1" step={CPH_STEP} value={cphHighDraft ?? cphRange.high} onChange={e => setCphHighDraft(e.target.value)} onBlur={commitCphDrafts} onKeyDown={e => {
                        if (e.key === 'Enter') e.currentTarget.blur();
                      }} aria-label="High end of ad spend cost per hire" style={{
                        width: `${fieldWidthCh(cphHighDraft ?? cphRange.high)}ch`
                      }} className="no-spinner inline-field text-2xl md:text-3xl font-bold text-navy bg-transparent border-none outline-none focus:text-amber transition-colors" data-id="element-2095" data-mp="300di7ms8m05e" />
                        </div>
                        <div className="absolute right-0 top-1/2 -translate-y-1/2 flex items-center gap-1 opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 transition-opacity duration-200" data-id="element-2096" data-mp="67vxhj43wrnt">
                          <button type="button" onClick={() => shiftCphRange(CPH_STEP)} className="flex h-6 w-6 items-center justify-center rounded-full bg-navy-light text-xs font-bold text-white shadow-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-amber" aria-label="Increase cost per hire range by $50" data-id="element-2097" data-mp="g74pw7jbpy82">
                            ↑
                          </button>
                          {cphRange.low > CPH_STEP && <button type="button" onClick={() => shiftCphRange(-CPH_STEP)} className="flex h-6 w-6 items-center justify-center rounded-full bg-navy-light text-xs font-bold text-white shadow-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-amber" aria-label="Decrease cost per hire range by $50" data-id="element-2098" data-mp="17xhtsgxpootg">
                              ↓
                            </button>}
                        </div>
                      </div>
                      <div className="mt-2 text-xs text-gray-600" data-id="element-2099" data-mp="374g7cchbgiji">
                        Modeled at the median: ${inputs.cph.toLocaleString()}
                      </div>
                    </div>
                  </div>

                  {/* Right Column: Expected Monthly Hires */}
                  <div data-id="element-2100" data-mp="3uusmo8ew5hk4">
                    <div className="expected-hires-card h-full p-4 md:p-6 rounded-lg bg-navy border border-amber flex flex-col justify-center" style={{
                  minHeight: '120px'
                }} data-id="element-2101" data-mp="3uv2dfy7rfsqv">
                      <div className="text-xs text-amber mb-2 font-extrabold uppercase tracking-wide" data-id="element-2102" data-mp="337r8ce559b71">
                        Expected Monthly Hires
                      </div>
                      <div className="text-3xl md:text-4xl font-extrabold text-white" data-id="element-2103" data-mp="2tgmr17rzxe91">
                        {hiresRange.low}&ndash;{hiresRange.high}
                      </div>

                    </div>
                  </div>
                </div>
              </div>

              {/* Rep-onomics — hidden until shown */}
              <div className="mb-8" data-id="element-2104" data-mp="384r49w8a4xkf">
                <RepEconomics inputs={calcInputs} results={results} halfSales={halfSales} doubleAttrition={doubleAttrition} data-id="element-2105" data-mp="387wqn3pixlcg" />
              </div>

              <button onClick={handleCalculate} className="w-full py-4 md:py-5 rounded font-bold text-base md:text-lg transition-all shadow-lg bg-amber hover:bg-amber-dark text-navy active:translate-y-[1px] cursor-pointer" data-id="element-2106" data-mp="21pgm8e1bysdf">
                Calculate My ROI →
              </button>
            </div>
          </div>}

        {/* Step 3: Results */}
        {hasCalculated && results && <div ref={resultsRef} className="scroll-mt-8" data-id="element-2107" data-mp="1y8c4xf3mji8w">
            <div className="flex items-center gap-3 mb-4 md:mb-6" data-id="element-2108" data-mp="1ahovgpe28kk8">
              <div className="w-10 h-10 rounded bg-amber flex items-center justify-center text-lg font-bold text-navy" data-id="element-2109" data-mp="1afvn95nstsa4">
                3
              </div>
              <h2 className="text-xl md:text-2xl font-bold text-navy" data-id="element-2110" data-mp="1k8tblsfh2bgn">
                Projected Investment and ROI
              </h2>
            </div>

            <div className="mb-4" data-id="element-2111" data-mp="rrqnsae6cth4">
              {inputs.pkg &&
          // Clicking the badge quietly swaps between the monthly and
          // legacy fee schedules. It looks the same either way, and a
          // reload always returns to the monthly schedule.
          <button type="button" onClick={() => setDeckPricingModel(deckPricingModel === 'flagship' ? 'monthly' : 'flagship')} className="inline-block cursor-default select-none bg-amber-light text-amber-dark px-4 py-2 rounded text-sm font-semibold border-2 border-amber/20 focus:outline-none" data-id="element-2112" data-mp="3fbiux00hxnna">
                  {results.preset.name}
                </button>}
            </div>

            <div className="bg-white border border-gray-200 rounded-xl p-5 md:p-7 shadow-lg mb-6" data-id="element-2113" data-mp="bw3mg37rnm49">
              <ResultsSummary results={results} halfSales={halfSales} doubleAttrition={doubleAttrition} timeframe={timeframe} onOptimizeForTarget={handleOptimizeForTarget} onOptimizeForTeamTarget={handleOptimizeForTeamTarget} onUndoOptimize={handleUndoOptimize} canUndoOptimize={preOptimizeMonthlyCampaigns !== null} revenueTrail={usesRevenueTrail(inputs.industry)} inputs={inputs} data-id="element-2114" data-mp="6z3gdsax2jd5" />
            </div>

            <div className="mb-6" data-id="element-2115" data-mp="jg4haeg5o25v">
              <ProjectionTable rows={results.rows} inputs={calcInputs} onInputsChange={setInputs} halfSales={halfSales} doubleAttrition={doubleAttrition} onTarget={onTarget} onToggleOnTarget={handleToggleOnTarget} lowRetention={lowRetention} onToggleLowRetention={handleToggleLowRetention} plusFive={plusFive} onPlusFive={handlePlusFive} timeframe={timeframe} setTimeframe={setTimeframe} data-id="element-2116" data-mp="jf36mb31xhoo" />
            </div>

            <div className="mb-6" data-id="element-2117" data-mp="1utgq73qrvayw">
              <ProfitChart rows={results.rows} breakeven={results.breakeven} halfSales={halfSales} setHalfSales={setHalfSales} doubleAttrition={doubleAttrition} setDoubleAttrition={handleDoubleAttritionChange} onTarget={onTarget} onToggleOnTarget={handleToggleOnTarget} lowRetention={lowRetention} onToggleLowRetention={handleToggleLowRetention} plusFive={plusFive} onPlusFive={handlePlusFive} tableTimeframe={timeframe} allowThreeYears={usesRevenueTrail(inputs.industry)} data-id="element-2118" data-mp="1texlfw5am825" />
            </div>

            <div className="flex flex-wrap gap-3 mb-6" data-id="element-2119" data-mp="1xfpuope60cyf">
              <button onClick={handleReset} className="px-3 py-2 rounded border-2 border-gray-300 text-gray-700 hover:border-gray-400 font-semibold text-xs transition-colors active:translate-y-[1px]" data-id="element-2120" data-mp="2uw6fqg3vfprc">
                Start Over
              </button>
            </div>
          </div>}
      </div>

      {/* Hiring Funnel Modal */}
      <HiringFunnelModal isOpen={showHiringFunnel} onClose={() => setShowHiringFunnel(false)} estimatedHires={reverseHires} data-id="element-2121" data-mp="37e8tnc9os54f" />
    </div>;
}